<template>
    <!-- ===================== Normal view type ===================== -->
    <main id="issues-new-screen">
        <b-container class="my-4" v-if="!isMobile">
            <b-row no-gutters>
                <b-col sm="12" md="12" lg="4" xl="3">
                    <div class="left-container w-100">
                        <div class="issues-tabs">
                            <div
                                v-for="(tab, index) in tabsOptions"
                                :key="index"
                                @click="!isLoading && changeTabContent(tab.value)"
                                :class="{ active: pagination_control.status == tab.value }"
                                class="tab d-flex justify-content-center align-items-center"
                            >
                                <p
                                    class="tab-title text-12 text-weight-bold"
                                    :class="[pagination_control.status == tab.value ? 'text-12 text-weight-bold' : 'text-12 text-secondary-3']"
                                >
                                    {{ tab.text }}
                                </p>
                                <p class="sup">{{ getCurrentTabCount(tab) }}</p>
                            </div>
                        </div>
                        <div class="issues-content">
                            <IssuesList
                                ref="issues-list"
                                @issueCountUpdated="issueCountUpdated"
                                @issueClick="issueClicked"
                                @issueFilterUpdated="issueFilterUpdated"
                                :selectedIssue="selectedIssue"
                                :status="pagination_control.status"
                                :project="project"
                                @setSelectedIssue="
                                    (id) => {
                                        id == -1 ? (selectedIssue = {}) : (selectedIssue = { id })
                                    }
                                "
                            >
                            </IssuesList>
                        </div>
                    </div>
                </b-col>
                <b-col sm="12" md="12" lg="8" xl="9">
                    <div v-if="selectedIssue && selectedIssue.id" class="right-container w-100">
                        <IssueContainer
                            :issueId="selectedIssue.id"
                            :issue_count="issue_count"
                            :issueOptions="issueOptions"
                            ref="issue-container"
                            @issueUpdated="updateList"
                            @onStatusChange="onSelectedIssueStatusChange"
                            @onIssueDeleted="onIssueDeleted"
                        />
                    </div>
                    <div v-else class="right-container w-100">
                        <div class="top-buttons d-flex justify-content-end w-100"></div>
                        <div class="no-issue-selected d-flex justify-content-center align-items-center">
                            <b-img :src="require('@/assets/img/no-issues.png')" fluid></b-img>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </main>
</template>

<script>
// =====================
// External
// =====================
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

// =====================
// UI Components
// =====================
import SelectFloorplanInput from '@/components/360Viewer/Sidebar/Utils/SelectFloorplanInput'
import Issue from '@/components/Issues/IssueItem'
import IssuesList from '@/components/Issues/IssuesList'
import IssueContainer from '@/components/Issues/IssueContainer'
import CustomMessageInput from '@/components/Utils/CustomMessageInput'
import IssueComments from '@/components/Issues/IssueComments'
import IssueHeader from '@/components/Issues/IssueHeader'

// ==========================
// Icons
// ==========================
import 'vue-awesome/icons/sort-amount-down'
import 'vue-awesome/icons/sort-amount-down-alt'
import 'vue-awesome/icons/filter'
import 'vue-awesome/icons/file-download'
import 'vue-awesome/icons/file-pdf'
import 'vue-awesome/icons/file-excel'
import 'vue-awesome/icons/chevron-left'

import { mapState, mapActions } from 'vuex'

export default {
    components: {
        Issue,
        IssuesList,
        IssueContainer,
        CustomMessageInput,
        IssueHeader,
        IssueComments,
        Treeselect,
        SelectFloorplanInput,
    },
    props: {
        locationType: String,
        currentFloorplan: Object,
        project_id: {
            type: Number,
            default: -1,
        },
        isOpen: Boolean,
        // Options
        subjectsOptions: {
            type: Array,
            default: () => {
                return []
            },
        },
        responsiblesOptions: {
            type: Array,
            default: () => {
                return []
            },
        },
        locationsOptions: {
            type: Array,
            default: () => {
                return []
            },
        },
        project: {
            type: Object,
            default: () => {
                return {}
            },
        },
    },
    computed: {
        ...mapState(['user', 'sidebar']),

        /**
         * Summary.
         * verify if there is some active filter. (Viewer)
         *
         * @returns {Boolean}
         */
        theresAFilterActive() {
            return Boolean(
                (this.issueOptions.filter &&
                    (this.issueOptions.filter.closed_end ||
                        this.issueOptions.filter.closed_start ||
                        this.issueOptions.filter.created_end ||
                        this.issueOptions.filter.created_start ||
                        this.issueOptions.filter.due_date_end ||
                        this.issueOptions.filter.due_date_start ||
                        this.issueOptions.filter.causes.length ||
                        this.issueOptions.filter.types.length ||
                        this.issueOptions.filter.created_by.length ||
                        this.issueOptions.filter.locations.length ||
                        this.issueOptions.filter.priorities.length ||
                        this.issueOptions.filter.responsibles.length ||
                        this.issueOptions.filter.subjects.length)) ||
                    this.pagination_control.search
            )
        },

        /**
         * Summary.
         * verify if the location filter is active. (Viewer)
         *
         * @returns {Boolean}
         */
        isLocationFilterActive: function () {
            return this.selectedFloorplanId && this.selectedFloorplanId != 'all'
        },

        cantGenerateNewPdf() {
            return (
                this.newReportOptions.type == null ||
                this.newReportOptions.filter_level == null ||
                (this.newReportOptions.filter_level == 'filtered' && this.newReportOptions.status == null) ||
                (this.newReportOptions.filter_level != 'single' && this.newReportOptions.group == null) ||
                this.loadingReport
            )
        },
    },
    watch: {
        // Trigger the location filter when the current location changes (Viewer)
        selectedFloorplanId: function () {
            this.filterLocation()
        },

        // Used to verify when user opens the sidebar for the first time to fetch data (Viewer)
        isOpen: function (newOne, oldOne) {
            if (!oldOne && newOne) {
                this.setup()
            }
        },
    },
    data() {
        return {
            searchTimeout: null,
            editModeEnabled: false,

            reportProgress: {},
            reportInstance: null,
            selected: null,

            isLoading: false,
            infiniteId: +new Date(),

            groups: [],
            members: [],
            floorplans: [],
            pagination_control: {
                page: 1,
                limit: 100,
                status: 'open',
                mine: false,
                search: null,
                group_field: 'due_date',
            },
            tabsOptions: [
                {
                    value: 'open',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_2'),
                    count: 0,
                },
                {
                    value: 'in_review',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_3'),
                    count: 0,
                },
                {
                    value: 'closed',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_4'),
                    count: 0,
                },
            ],
            orderByOptions: [
                {
                    value: 'priority',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_5'),
                    id: 0,
                },
                {
                    value: 'created_at',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_6'),
                    id: 1,
                },
                {
                    value: 'updated_at',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_7'),
                    id: 2,
                },
            ],
            isFilterDropdownOpen: false,
            isOrderByDropdownOpen: false,
            currentOrderBy: 0,
            selectedIssue: {},
            issues: [],
            issue_count: {},
            issue_filter_count: {},
            // ORDER BY
            // 0 - priority
            // 1 - created_at
            // 2 - updated_at
            orderByFilter: 0,
            issueOptions: {
                order: {
                    field: 'priority',
                },
            },
            infiniteScrollState: null,
            filterCreatedBy: [],

            isFirstRequest: true,

            //Issue returned by getIssue
            issueQuery: {},
            issueInfoOptions: {},

            // New report options
            loadingReport: false,
            newReportOptions: {
                filter_level: null,
                status: null,
                group: 'none',
                type: null,
            },

            // Viewer especific data
            selectedFloorplanId: null,
            messageInputFilePreviewsActive: false,
            isMobile: false,
        }
    },
    methods: {
        issueCountUpdated({ issueCount, issueFilterCount }) {
            this.issue_filter_count = issueFilterCount
            this.tabsOptions[0].count = issueCount.open
            this.tabsOptions[1].count = issueCount.in_review
            this.tabsOptions[2].count = issueCount.closed
            this.issue_count = issueCount
        },
        issueFilterUpdated(issueFilter, issueOrder) {
            this.issueOptions = {
                ...this.issueOptions,
                filter: issueFilter,
                order: issueOrder,
            }
        },
        issueClicked(data) {
            this.selectedIssue = data
        },
        changeTabContent(status) {
            this.pagination_control.status = status
        },

        updateList() {
            this.$refs['issues-list'].updateList()
        },
        onIssueDeleted(id) {
            this.$refs['issues-list'].onIssueDeleted(id)
        },
        onSelectedIssueStatusChange({ issue, status, old_status }) {
            this.pagination_control.status = status
            this.$refs['issues-list'].updateList()
        },
        getCurrentTabCount(tab) {
            if (this.issue_filter_count[tab.value] != tab.count && this.issue_filter_count[tab.value] != 0) {
                if (this.theresAFilterActive) {
                    return (this.issue_filter_count[tab.value] ? this.issue_filter_count[tab.value] : 0) + ' ' + this.$t('keyword26') + ' ' + tab.count
                } else {
                    return this.issue_filter_count[tab.value] ? this.issue_filter_count[tab.value] : 0
                }
            } else return tab.count
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/views/Project/Issues/issues-new-screen.scss';

@import '@/scss/components/360Viewer/Sidebar/generic-sub-sidebar.scss';
@import '@/scss/components/360Viewer/Sidebar/Issues/issues-sidebar.scss';
</style>
